/**
 * 허브 명소 필터 데이터 (09-23).
 *
 * `landmarks-jp.json` 은 OpenStreetMap(© OpenStreetMap contributors, ODbL)에서 Bestbike
 * 지점이 있는 권역(수도권·간사이·주고쿠·규슈)의 명소·역·동네·공항을 모으고, 위키데이터로
 * 다국어 이름과 인지도(위키백과 언어 수 = `rank`)를 붙여 추린 정적 목록이다.
 * 생성 스크립트: ~/locarora-previews/landmarks/(collect → refine → extra → build).
 * 파일은 권역 → 탭 → 인지도 내림차순으로 이미 정렬돼 있다.
 *
 * 명소를 고르면 거리 기준점이 그 명소가 된다 — 지점 목록이 가까운 순으로 바뀌고
 * 행에 "명소에서 N km"가 붙는다(직선거리).
 */
import type { Locale } from "@/i18n/routing";
import DATA from "./landmarks-jp.json";

export const LANDMARK_KINDS = ["sight", "station", "area", "airport"] as const;
export type LandmarkKind = (typeof LANDMARK_KINDS)[number];

export interface Landmark {
  /** 위키데이터 QID — URL(`lm`)에 싣는 안정 키 */
  id: string;
  region: string;
  kind: LandmarkKind;
  lat: number;
  lng: number;
  /** 인지도(위키백과 언어 수) — 탭 안 정렬 기준 */
  rank: number;
  name: Record<Locale, string>;
}

export const LANDMARKS = DATA as Landmark[];

const BY_ID = new Map(LANDMARKS.map((l) => [l.id, l]));

/** 권역이 "지금 보는 곳"이라고 볼 최대 거리(km). 넘으면 명소 필터를 숨긴다. */
const REGION_MAX_KM = 150;

export function getLandmark(id: string | null | undefined): Landmark | null {
  return id ? (BY_ID.get(id) ?? null) : null;
}

export function landmarkName(landmark: Landmark, locale: string): string {
  return landmark.name[locale as Locale] ?? landmark.name.ja;
}

/**
 * 좌표가 속한 권역 — 가장 가까운 명소의 권역. 150km 안에 명소가 없으면(다른 나라·
 * 권역 밖) null 이고, 그땐 명소 알약을 그리지 않는다.
 */
export function regionAt(lat: number, lng: number): string | null {
  let best: { region: string; km: number } | null = null;
  for (const l of LANDMARKS) {
    const km = haversineKm(lat, lng, l.lat, l.lng);
    if (!best || km < best.km) best = { region: l.region, km };
  }
  return best && best.km <= REGION_MAX_KM ? best.region : null;
}

/** 권역 표시 순서(= 지점이 많은 순). */
export const LANDMARK_REGIONS = ["kanto", "kansai", "chugoku", "kyushu"] as const;

/**
 * 가장 가까운 권역 — 거리 제한 없음. 명소 피커의 기본 지역(사용자가 바꿀 수 있다)이라
 * 일본 전체를 보고 있어도 항상 하나를 돌려준다.
 */
export function nearestRegion(lat: number, lng: number): string {
  let best: { region: string; km: number } | null = null;
  for (const l of LANDMARKS) {
    const km = haversineKm(lat, lng, l.lat, l.lng);
    if (!best || km < best.km) best = { region: l.region, km };
  }
  return best?.region ?? LANDMARK_REGIONS[0];
}

/** 권역 × 탭 목록(인지도 순). */
export function landmarksOf(region: string, kind: LandmarkKind): Landmark[] {
  return LANDMARKS.filter((l) => l.region === region && l.kind === kind);
}

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) *
      Math.cos((bLat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
